import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";

// Material UI
import { Typography, Divider } from "@mui/material";

// Componentes
import AdminLayout from "@components/MainPage/AdminLayout";
import Map from "@components/Maps/CustomComponents/GenericMap";
import Colors from "@components/Maps/InternalComponents/Cards/Colors";
import Filter from "@components/MapsModules/Routes/Filter";
import PolygonsCard from "@components/MapsModules/Routes/PolygonsCard";

// Otros
import CatalogService from "@services/CatalogServices";
import RouteService from "@services/MapServices/RouteServices";
import geojson_ex_pol_data from "@data/polygons/poligonos.json";
import { setVars, getVars } from "@utils/global";

// Estilos de mapas
import { style_original_rutas, style_rutas } from "@components/Maps/Auxiliars/ColorFeature";

const elements = {
  seccion: [
    { title: "Seccion:", name: "seccion", type: "text" },
    { title: "Ruta:", name: "Ruta", type: "text" },
    { title: "Tipo:", name: "TipoSeccion", type: "text" },
    { title: "DLOC:", name: "DLOC", type: "text" },
    { title: "DFED:", name: "DFED", type: "text" },
    { title: "LNOM:", name: "Lnom", type: "text" },
    { title: "Casillas:", name: "TotalCasillas", type: "text" },
  ],
  dfed: [{ title: "DFED:", name: "dfed", type: "text" }],
};

const infoGeneralElements = [
  { title: "Municipio:", name: "MunicipioReportes", color: "#000", type: "text" },
  { title: "Total Rutas:", name: "TotalRutas", color: "#000", type: "text" },
  { title: "Total Secciones:", name: "TotalSecciones", color: "#000", type: "text" },
  { title: "Total Casillas:", name: "TotalCasillas", color: "#000", type: "text" },
  { title: "C/Con Ruta:", name: "TotalCasillasOcupadas", color: "#000", type: "text" },
  { title: "C/Sin Ruta:", name: "TotalCasillasLibres", color: "#000", type: "text" },
];

const colorElements = [
  { title: "Sin asignar", color: "rgba(0, 102, 255, 0.4)" },
  { title: "Faltantes", color: "rgba(255, 118, 52, 0.4)" },
  { title: "Completadas", color: "rgba(75, 181, 67, 0.5)" },
];

const Maps = () => {
  const { user } = getVars("Token");

  const [polygons, setPolygons] = useState({ type: "FeatureCollection", features: [] });
  const [polygonsOLD, setPolygonsOLD] = useState(null);

  const [selectedSectionsProperties, setSelectedSectionsProperties] = useState([]);
  const [valuesInfoGeneral, setValuesInfoGeneral] = useState(null);

  const [filter, setFilter] = useState({ idMunicipio: "", idDF: "", idDL: "" });

  const [catalogs, setCatalogs] = useState({ municipios: [], DF: [], DL: [] });
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(true);

  useEffect(() => {
    setInitialVars();
    getEmptyMap();
    loadCatalogs();
    // eslint-disable-next-line
  }, []);

  const setInitialVars = (values) => {
    const item = values ?? [];

    if (!item.includes("itemEdit")) setVars("itemEdit", null);
    if (!item.includes("selectedSections")) setVars("selectedSections", []);
    if (!item.includes("selectedSectionsProperties")) setVars("selectedSectionsProperties", []);
    if (!item.includes("selectedDFED")) setVars("selectedDFED", null);
    if (!item.includes("flagCreatePolygon")) setVars("flagCreatePolygon", false);
    if (!item.includes("valueTab")) setVars("valueTab", 0);
    if (!item.includes("filterRoutes"))
      setVars("filterRoutes", { idMunicipioReportes: "", idDF: "", idDL: "" });
  };

  const loadCatalogs = useCallback(() => {
    let catalogsParams = [{ id: "municipios_dl", getAll: false }];
    CatalogService.getCatalogs(catalogsParams)
      .then((res) => {
        if (res.results) {
          if (res.response.errors.length > 0) {
            Swal.fire({
              title: "Algunos catálogos NO pudieron ser cargados. Contacte al administrador",
              icon: "warning",
            });
          }

          const catalogsResponse = res.response.catalogs;
          setCatalogs((prevState) => {
            return {
              ...prevState,
              municipios: catalogsResponse.municipios_dl ?? [],
            };
          });
        } else {
          Swal.fire({ title: res.message, icon: "warning" });
        }
      })
      .catch((e) => {
        Swal.fire({ title: e.message, icon: "warning" });
      })
      .finally(() => {
        setIsLoadingCatalogs(false);
      });
  }, []);

  const getEmptyMap = () => setPolygons({ type: "FeatureCollection", features: [] });

  const handleFilter = async (_filter, polygon) => {
    let data_parameters = {};
    if (!polygon)
      data_parameters = {
        page: 0,
        pageSize: 10,
        filtered: _filter,
        idEntidad: user.idEstado,
      };
    else
      data_parameters = {
        polygon,
        page: 0,
        pageSize: 10,
        filtered: _filter,
        idEntidad: user.idEstado,
      };
    setFilter(_filter);
    setPolygons(null);

    try {
      const result = await RouteService.getSections(data_parameters);
      const { results, response, message } = result;

      if (results) setPolygons(response.data);
      else {
        Swal.fire({ title: message, icon: "warning" });
        setVars("flagCreatePolygon", false);
        setPolygons({ type: "FeatureCollection", features: [] });
      }
    } catch (error) {
      Swal.fire({ title: error.message, icon: "warning" });
    }

    setInitialVars(["filterRoutes", "flagCreatePolygon"]);
    setSelectedSectionsProperties([]);

    // En caso de solo filtrar por municipio
    if (_filter.length === 1) {
      const data_pol_old = geojson_ex_pol_data.features.filter(
        (item) => item.properties.idMunicipioReportes === _filter[0].value
      );
      setPolygonsOLD({ type: "FeatureCollection", features: data_pol_old });

      try {
        const result = await RouteService.getInformationMunicipality({
          page: 0,
          pageSize: 10,
          filtered: _filter,
        });
        const { results, response, message } = result;

        if (results) {
          if (response.data.length !== 0) {
            setValuesInfoGeneral(response.data[0]);
          } else Swal.fire({ title: message, icon: "warning" });
        } else Swal.fire({ title: message, icon: "warning" });
      } catch (error) {
        Swal.fire({ title: error.message, icon: "warning" });
      }
    }
  };

  const handleRefreshShape = () => {
    setSelectedSectionsProperties([]);
    handleFilter(filter);
  };

  const handleChangeSections = (array) => setSelectedSectionsProperties(array);

  const FilterComponent = (
    <>
      <Filter
        catalogs={catalogs}
        setCatalogs={setCatalogs}
        loadingCatalogs={isLoadingCatalogs}
        handleFilter={handleFilter}
        width={"100%"}
      />

      {polygons?.features.length !== 0 && getVars("flagCreatePolygon") && (
        <>
          <Divider sx={{ marginY: 1 }} />

          <Typography variant="h6" fontSize={16} align="center">
            Rutas
          </Typography>

          <PolygonsCard
            permissions={{
              Ver: 1,
              Agregar: 1, //middleware.checkMenuActionId("Agregar"),
              Editar: 1, //middleware.checkMenuActionId("Editar"),
              Eliminar: 1, //middleware.checkMenuActionId("Eliminar")
            }}
            polygons={polygons}
            setPolygons={setPolygons}
            selectedSectionsProperties={selectedSectionsProperties}
            handleRefreshShape={handleRefreshShape}
            handleEmptyProperties={() => {
              setVars("selectedSections", []);
              setVars("selectedSectionsProperties", []);
              setSelectedSectionsProperties([]);
            }}
          />
        </>
      )}
    </>
  );

  const cardComponent = getVars("flagCreatePolygon")
    ? { component: <Colors elements={colorElements} height="28px" spacing={1} />, title: "Colores" }
    : null;

  // Se utilizan nuevas funciones evitando usar las defaults
  const onEachFeature = (feature, layer, click, params) => {
    const properties = feature.properties;

    if (properties[properties.shp] !== undefined) {
      layer.bindTooltip(properties[properties.shp] + "", { permanent: true, direction: "center" });
      layer.setStyle(style_original_rutas);

      layer.on({
        click: click,
      });

      // En caso de agregar la propiedad highlightFeature
      if (properties.highlightFeature) {
        const flagCreatePolygon = getVars("flagCreatePolygon");

        // Si no hay municipio y DF en el filtro
        if (!flagCreatePolygon) return true;

        const DFED = getVars("selectedDFED");
        // La sección tiene diferente DFED
        if (DFED !== null && properties?.DFED !== DFED) return true;

        if (properties.seccion) {
          // Recuperar el array selectedSections del storage para revisar si el feature se encuentra dentro
          const selectedSections = getVars("selectedSections");
          const selectedSectionsProperties = getVars("selectedSectionsProperties");

          // Agregamos el DFED al storage
          if (selectedSections.length === 0) setVars("selectedDFED", properties.DFED);

          // Busca la seccion del feature seleccionado en el array que tenemos
          const found = selectedSections.find((element) => element === properties.seccion);

          // Si no se encuentra se agrega
          if (found === undefined) {
            if (!params.L.Browser.ie && !params.L.Browser.opera && !params.L.Browser.edge)
              layer.bringToFront();

            layer.setStyle(style_rutas(layer.feature, true));

            selectedSections.push(properties.seccion);
            selectedSectionsProperties.push(properties);

            setVars("selectedSections", selectedSections);
            setVars("selectedSectionsProperties", selectedSectionsProperties);
          }

          // Añadimos el nuevo array al state
          const array_properties = getVars("selectedSectionsProperties");

          handleChangeSections(array_properties);
        } else {
          if (!params.L.Browser.ie && !params.L.Browser.opera && !params.L.Browser.edge) layer.bringToFront();
          layer.setStyle(style_rutas(layer.feature, true));
        }
      }
    }
  };

  const highlightFeature = (e, params) => {
    const layer = e.target;
    const properties = layer.feature.properties;

    if (params.selectedFeature && !properties.seccion) params.resetHighlight(params.selectedFeature);

    params.setFeature(layer.feature);
    params.selectedFeature = e;

    const flagCreatePolygon = getVars("flagCreatePolygon");
    const tab = getVars("valueTab");

    // Si no hay municipio y DF en el filtro
    if (!flagCreatePolygon) return true;

    // Si esta en buscar
    if (tab === 1) return true;

    // Si no tiene casillas
    if (properties.casillas.length === 0) return true;

    const DFED = getVars("selectedDFED");
    // La sección tiene diferente DFED
    if (DFED !== null && properties?.DFED !== DFED) {
      //layer.bindTooltip("La sección tiene diferente DFED", { permanent: false, direction: "center" });
      return true;
    }

    if (properties.seccion) {
      // Recuperar el array selectedSections del storage para revisar si el feature se encuentra dentro
      const selectedSections = getVars("selectedSections");
      const selectedSectionsProperties = getVars("selectedSectionsProperties");

      // Agregamos el DFED al storage
      if (selectedSections.length === 0) setVars("selectedDFED", properties.DFED);

      // Busca la seccion del feature seleccionado en el array que tenemos
      const found = selectedSections.find((element) => element === properties.seccion);

      // Si no se encuentra se agrega
      if (found === undefined) {
        if (!params.L.Browser.ie && !params.L.Browser.opera && !params.L.Browser.edge) layer.bringToFront();

        layer.setStyle(style_rutas(layer.feature, true));

        selectedSections.push(properties.seccion);
        selectedSectionsProperties.push(properties);
        setVars("selectedSections", selectedSections);
        setVars("selectedSectionsProperties", selectedSectionsProperties);
      } else {
        // Si lo encuentra, lo quita
        const selectedSectionsTemp = selectedSections.filter((element) => element !== properties.seccion);
        setVars("selectedSections", selectedSectionsTemp);

        const selectedSectionsPropertiesTemp = selectedSectionsProperties.filter(
          (element) => element.seccion !== properties.seccion
        );
        setVars("selectedSectionsProperties", selectedSectionsPropertiesTemp);

        params.resetHighlight(e);
      }

      // Añadimos el nuevo array al state
      const array_properties = getVars("selectedSectionsProperties");

      handleChangeSections(array_properties);
    } else {
      if (!params.L.Browser.ie && !params.L.Browser.opera && !params.L.Browser.edge) layer.bringToFront();
      layer.setStyle(style_rutas(layer.feature, true));
    }
  };

  return (
    <AdminLayout delPadding>
      <Map
        data={polygons}
        polygonsOLD={polygonsOLD}
        drawerLeft={{ open: true, component: FilterComponent, width: { sm: 300, md: 350, lg: 500, xl: 600 } }}
        cardInfo={{ initialValues: { title: "INFORMACIÓN", elements: elements[polygons?.shp ?? "dfed"] } }}
        infoGeneral={{
          values: valuesInfoGeneral,
          initialValues: { title: "RESUMEN MUNICIPAL", elements: infoGeneralElements },
        }}
        cardComponent={cardComponent}
        highlightFeature={highlightFeature}
        onEachFeature={onEachFeature}
        centerByFeatures={true}
        styleProp={{
          data: style_original_rutas,
          polygonsOLD: style_original_rutas,
          highlightFeature: style_rutas,
        }}
      />
    </AdminLayout>
  );
};

export default Maps;
